/**
 * Application Configuration Constants
 *
 * Centralized configuration for marketing app settings.
 * Update these values to change app-wide behavior without modifying component code.
 */

export const APP_CONFIG = {
  /**
   * Trial Configuration
   */
  TRIAL_DAYS: 30,

  /**
   * Pricing Configuration
   */
  PRICE_PER_STUDENT: 10, // Price in INR per student per month
  DEFAULT_ESTIMATED_STUDENTS: 100,

  /**
   * Form Validation Timeouts
   */
  TENANT_ID_AVAILABILITY_DEBOUNCE: 1000, // milliseconds

  /**
   * Email Configuration
   */
  SUPPORT_EMAIL: 'enquiry@kalaivani.in',
  SUPPORT_PHONE: '75501 96844',

  /**
   * Demo Configuration
   */
  DEMO_EXPIRY_DAYS: 7,
  DEMO_BASE_URL: 'https://demo.kalaivani.in',

  /**
   * WhatsApp Configuration
   */
  WHATSAPP_PHONE: '917550196844', // Country code + number without spaces

  /**
   * Social Media Configuration
   */
  SOCIAL_MEDIA: {
    FACEBOOK: 'https://www.facebook.com/kalaivani-erp',
    LINKEDIN: 'https://www.linkedin.com/company/kalaivani-erp',
    YOUTUBE: 'https://www.youtube.com/@kalaivani-erp',
    TWITTER: 'https://twitter.com/kalaivani-erp',
    INSTAGRAM: 'https://www.instagram.com/kalaivani-erp'
  },

  /**
   * Validation Patterns
   */
  PATTERNS: {
    TENANT_ID: /^[a-z0-9_-]{3,30}$/,
    PINCODE: /^\d{6}$/,
    EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    PHONE: /^[\d\s\+\-\(\)]{10,15}$/
  },

  /**
   * Image Assets Configuration
   * All image references are centralized here for easy management
   */
  IMAGES: {
    // Logo
    LOGO: '/logo.png',

    // Hero Carousel Images (1:1 aspect ratio recommended)
    HERO_CAROUSEL: [
      '/images/hero/easy-admission.jpg',
      '/images/hero/easy-attendance.jpg',
      '/images/hero/easy-communication.jpg',
      '/images/hero/easy-reports.jpg',
      '/images/hero/easy-communication.jpg',
      '/images/hero/easy-reports.jpg',
      '/images/hero/easy-communication.jpg',
    ],

    // Marketing Callout Image
    MARKETING_CALLOUT: '/images/features.jpg',

    // Page Header Background Images (16:9 or 16:6 aspect ratio recommended)
    PAGE_HEADERS: {
      SIGNUP: '/images/features.jpg',
      FEATURES: '/images/features.jpg',
      BENEFITS: '/images/benefits.jpg',
      CONTACT: '/images/contact.jpg',
      PRICING: '/images/pricing.jpg',
      COOKIE: '/images/pricing.jpg',
      PRIVACY: '/images/pricing.jpg',
      TERMS: '/images/pricing.jpg'
    }
  },

  /**
   * SEO Configuration
   * Base URL and default SEO settings
   * 
   * IMPORTANT: OG Image Requirements for Social Sharing:
   * - Size: 1200x630px (recommended aspect ratio 1.91:1)
   * - File size: < 600 KB (for WhatsApp compatibility)
   * - Format: JPG or PNG
   * - Should include: Clear headline and call-to-action text
   * - logo-og.png should be optimized to meet these requirements
   */
  SEO: {
    BASE_URL: 'https://kalaivani.in',
    DEFAULT_IMAGE: 'https://kalaivani.in/logo-og.png', // OG Image: 1200x630px, < 600 KB, with headline & CTA
    SQUARE_IMAGE: 'https://kalaivani.in/logo.png', // For Twitter cards (1200x1200px recommended)
    SITE_NAME: 'Kalaivani ERP',
    DEFAULT_AUTHOR: 'Kalaivani',
    DEFAULT_KEYWORDS: 'schools management software, Kalaivani ERP, school ERP, school management system, preschool management software, education management software, school administration software, attendance management, daily reports, admission management, exam report cards, fee collection software',
    DEFAULT_DESCRIPTION: 'Kalaivani ERP - Best schools management software. Manage admissions, attendance, daily reports, exams, fees, and communication. Start your 30-day free trial today.',
    TWITTER_HANDLE: '@kalaivani-erp'
  },

  /**
   * Advertising Tracking Configuration
   * Tracking IDs should be configured via environment variables
   * These are placeholder values - update in environment.ts
   */
  TRACKING: {
    GOOGLE_ADS_ID: '', // Set via environment.tracking.googleAdsId (production only)
    META_PIXEL_ID: '', // Will be set via environment
    GOOGLE_TAG_MANAGER_ID: '', // Optional, will be set via environment
    CONVERSION_EVENTS: {
      SIGNUP: 'signup',
      DEMO_REQUEST: 'demo_request',
      CONTACT: 'contact',
      PAGE_VIEW: 'page_view'
    },
    /**
     * CTA and Contact Action Events - track user interactions with CTAs and contact links
     */
    WHATSAPP_CLICK: 'whatsapp_click',
    PHONE_CLICK: 'phone_click',
    EMAIL_CLICK: 'email_click',
    DEMO_CTA_CLICK: 'demo_cta_click',
    /**
     * Google Ads Conversion Labels
     * Set these to your actual conversion labels from Google Ads
     * Format: 'AW-CONVERSION_ID/CONVERSION_LABEL'
     */
    GOOGLE_ADS_CONVERSION_LABELS: {
      SIGNUP: 'AW-17947988030/-yYGCKj9pvkbEL6gou5C',
      DEMO_REQUEST: 'AW-17947988030/9S_PCKv9pvkbEL6gou5C',
      CONTACT: 'AW-17947988030/BjELCK79pvkbEL6gou5C',
    },
    /**
     * Signup Funnel Steps - track user progression through signup flow
     */
    SIGNUP_FUNNEL: {
      STEP_CONTACT_INFO: 'signup_step_contact_info',
      STEP_SCHOOL_NAME: 'signup_step_school_name',
      STEP_SCHOOL_DETAILS: 'signup_step_school_details',
      STEP_REVIEW: 'signup_step_review',
      COMPLETED: 'signup_completed'
    },
    /**
     * Form Interaction Events - track form engagement
     */
    FORM_EVENTS: {
      FORM_START: 'form_start',
      FORM_SUBMIT: 'form_submit',
      FORM_ERROR: 'form_error'
    }
  }
} as const;
